import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, CheckCircle2, Download, Phone, Mail, ShieldCheck, Award, Cog, Layers, Wrench, Package, Atom, Leaf, BadgeCheck, Globe2, Sparkles, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useData } from "../DataContext";
import { LazyImage } from "../components/LazyImage";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildProductLD, buildBreadcrumbLD, buildProductFAQLD } from "../lib/jsonld";

const PIPE_CATEGORIES = ["plumbing", "drainage", "agriculture"];
const CHIP_ICONS = { ShieldCheck, Award, Cog, Atom, Leaf, BadgeCheck, Globe2, Sparkles, Wrench };
const DEFAULT_CHIPS = [
  { icon: "ShieldCheck", label: "Lead-Free" },
  { icon: "Award", label: "Warranty" },
  { icon: "Cog", label: "ISO Certified" }
];

export default function ProductDetail() {
  const { PRODUCT_CATEGORIES, getProductBySlug, getInstallationSteps, getSkuCatalogue, getSkuLongDesc, getSkuTypes, getSkuSizes } = useData();
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState("features");
  const [skuFilter, setSkuFilter] = useState("All");
  const [selectedSku, setSelectedSku] = useState(null);

  const showProductsTab = product && PIPE_CATEGORIES.includes(product.categoryId);

  usePageMeta(
    product ? (product.series ? `${product.name} — ${product.series} Series` : product.name) : "Product Not Found",
    product ? (product.tagline ? `${product.tagline} ${product.desc}` : product.desc) : undefined
  );

  // Preload all SKU images when the Products tab is mounted so toggling is instant
  useEffect(() => {
    if (!showProductsTab) return;
    const material = product.material || "uPVC";
    const catalogue = getSkuCatalogue(material);
    catalogue.forEach((sku) => {
      if (sku.image) {
        const i = new Image();
        i.src = sku.image;
      }
    });
  }, [showProductsTab, product]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setActiveImg(0);
    setActiveTab(showProductsTab ? "products" : "features");
    setSkuFilter("All");
  }, [slug, showProductsTab]);

  if (!product) {
    return (
      <div className="pt-[88px] md:pt-[112px] min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-black text-[#0a2a5e] mb-3">Product not found</h1>
          <p className="text-slate-600 mb-6">The product you're looking for doesn't exist or has been moved.</p>
          <Button asChild className="bg-[#0a2a5e] hover:bg-[#061a3d] text-white rounded-full">
            <Link to="/products"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  const category = PRODUCT_CATEGORIES.find((c) => c.id === product.categoryId);
  const related = category?.products.filter((p) => p.slug !== product.slug).slice(0, 4) || [];

  const productLd = buildProductLD(product);
  const faqLd = buildProductFAQLD(product);
  const breadcrumbLd = buildBreadcrumbLD([
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: product.categoryName, path: `/products#${product.categoryId}` },
    { name: product.name, path: `/products/${product.slug}` },
  ]);

  return (
    <div className="pt-[88px] md:pt-[112px]">
      <JsonLd blocks={[productLd, faqLd, breadcrumbLd]} id={`product-${product.slug}`} />
      {/* breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
          <Link to="/" className="hover:text-sky-600">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/products" className="hover:text-sky-600">Products</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/products#${product.categoryId}`} className="hover:text-sky-600">{product.categoryName}</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-[#0a2a5e] font-semibold">{product.name}</span>
        </div>
      </div>

      {/* MAIN PRODUCT */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          {/* Image */}
          <div>
            <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-slate-100 aspect-square border border-slate-200 flex items-center justify-center p-6">
              <img src={product.image} alt={product.name} className="max-w-full max-h-full w-auto h-auto object-contain" />
            </div>
          </div>

          {/* Info */}
          <div>
            <Link to={`/products#${product.categoryId}`} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold mb-4 hover:bg-sky-200 transition-colors">
              <Layers className="h-3.5 w-3.5" /> {product.categoryName}
            </Link>
            <h1 className="text-4xl md:text-5xl font-black text-[#0a2a5e] leading-tight mb-3">{product.name}</h1>
            <p className="text-xl text-sky-600 font-semibold mb-5">{product.tagline || product.desc}</p>
            <p className="text-slate-600 leading-relaxed mb-7">{product.longDesc || product.desc}</p>

            {/* highlight chips */}
            <div className="grid grid-cols-3 gap-3 mb-7">
              {(product.chips || DEFAULT_CHIPS).map((chip, i) => {
                const Icon = CHIP_ICONS[chip.icon] || ShieldCheck;
                return (
                  <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-center">
                    <Icon className="h-5 w-5 mx-auto text-sky-600 mb-1.5" />
                    <div className="text-xs font-semibold text-slate-700 leading-tight">{chip.label}</div>
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button asChild size="lg" className="bg-gradient-to-r from-sky-500 to-[#0a2a5e] text-white rounded-full px-7 shadow-md">
                <Link to="/contact">Enquire Now <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
              </Button>
              <Button variant="outline" size="lg" onClick={() => alert("Brochure download (mock)")} className="rounded-full px-7 border-[#0a2a5e] text-[#0a2a5e] hover:bg-[#0a2a5e] hover:text-white">
                <Download className="h-4 w-4 mr-1.5" /> Brochure
              </Button>
            </div>

            <div className="flex flex-wrap gap-5 text-sm pt-5 border-t border-slate-200">
              <a href="tel:18002023360" className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors">
                <Phone className="h-4 w-4 text-sky-500" /> 1800 202 3360
              </a>
              <a href="mailto:contact@baishalii.in" className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors">
                <Mail className="h-4 w-4 text-sky-500" /> contact@baishalii.in
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TABBED DETAILS */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2 border-b border-slate-200 mb-8 overflow-x-auto scrollbar-hide">
            {[
              ...(showProductsTab ? [{ id: "products", label: "Products" }] : []),
              { id: "features", label: "Features" },
              { id: "applications", label: "Applications" },
              { id: "specs", label: "Specifications" },
              { id: "installation", label: "Installation" }
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`shrink-0 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === t.id ? "border-sky-500 text-sky-600" : "border-transparent text-slate-600 hover:text-sky-600"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "products" && showProductsTab && (() => {
            const material = product.material || "uPVC";
            const catalogue = getSkuCatalogue(material);
            const filtered = skuFilter === "All" ? catalogue : catalogue.filter((s) => s.type === skuFilter);
            return (
              <div>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-black text-[#0a2a5e] mb-1">Explore our {material} Pipes &amp; Fittings Range</h3>
                    <p className="text-slate-600">A complete portfolio of {material} pipes, fittings, valves and solvent cements to build every BAISHALII system.</p>
                  </div>
                  <span className="text-sm text-slate-500 shrink-0"><strong className="text-[#0a2a5e]">{filtered.length}</strong> of {catalogue.length} SKUs</span>
                </div>

                {/* filter chips */}
                <div className="flex flex-wrap gap-2 mb-7">
                  {getSkuTypes(material).map((t) => (
                    <button
                      key={t}
                      onClick={() => setSkuFilter(t)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${skuFilter === t ? "bg-[#0a2a5e] text-white shadow-md" : "bg-white text-slate-700 hover:bg-sky-50 hover:text-sky-700 border border-slate-200"}`}
                    >
                      {t}
                      {t !== "All" && (
                        <span className="ml-1.5 text-[10px] opacity-70">
                          {catalogue.filter((s) => s.type === t).length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {filtered.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                    <Package className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No SKUs in this category yet.</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filtered.map((sku, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedSku({ ...sku, _material: material })}
                        className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-sky-400 hover:shadow-2xl hover:shadow-sky-500/10 hover:-translate-y-1.5 hover:ring-1 hover:ring-sky-200/60 transition-all duration-300 ease-out text-left will-change-transform"
                      >
                        <div className="aspect-square overflow-hidden bg-slate-50 relative">
                          <LazyImage src={sku.image} alt={sku.name} className="w-full h-full object-contain p-4 group-hover:scale-110 group-hover:-rotate-1 transition-transform duration-700 ease-out" />
                          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-white/95 text-[#0a2a5e] shadow-sm">{sku.type}</span>
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-[#0a2a5e] text-sm leading-snug mb-1 line-clamp-2 min-h-[36px] group-hover:text-sky-600 transition-colors">{sku.name}</h4>
                          <div className="text-[11px] text-sky-600 font-semibold mb-2">{sku.grade}</div>
                          <p className="text-xs text-slate-500 leading-snug line-clamp-2 mb-3 min-h-[32px]">{sku.desc}</p>
                          <span className="text-xs font-semibold text-[#0a2a5e] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                            View Details <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-8 p-5 rounded-xl bg-gradient-to-r from-sky-50 to-white border border-sky-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-sm text-slate-600"><strong className="text-[#0a2a5e]">Looking for a specific fitting?</strong> Our team can ship across India in 48 hours.</p>
                  <Button asChild size="sm" className="bg-[#0a2a5e] hover:bg-[#061a3d] text-white rounded-full self-start sm:self-auto">
                    <Link to="/contact">Request Quote <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                  </Button>
                </div>
              </div>
            );
          })()}

          {activeTab === "features" && (
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
              {(product.features || []).map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-5 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all">
                  <CheckCircle2 className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                  <p className="text-slate-700 leading-relaxed">{f}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === "applications" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl">
              {(product.applications || []).map((a, i) => (
                <div key={i} className="p-6 bg-white rounded-xl border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-sky-500 to-[#0a2a5e] flex items-center justify-center text-white font-black mb-3">
                    {i + 1}
                  </div>
                  <p className="font-semibold text-[#0a2a5e]">{a}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === "specs" && (
            <div className="max-w-6xl space-y-8">
              {(!product.specTable && (product.specs || []).length > 0) && (
                <div className="max-w-3xl">
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full">
                      <tbody>
                        {(product.specs || []).map(([k, v], i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                            <td className="px-6 py-4 text-sm font-semibold text-[#0a2a5e] w-1/3 border-b border-slate-100">{k}</td>
                            <td className="px-6 py-4 text-sm text-slate-700 border-b border-slate-100">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {product.specTable && product.specTable.variant === "cpvc" && (
                <div>
                  <h3 className="text-lg md:text-xl font-black text-[#0a2a5e] mb-1">{product.specTable.title}</h3>
                  {product.specTable.subtitle && (
                    <p className="text-sm text-slate-500 mb-4">{product.specTable.subtitle}</p>
                  )}
                  <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto bg-white">
                    <table className="w-full text-sm min-w-[1000px] border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200">
                      <thead>
                        <tr className="bg-slate-100 text-[#0a2a5e]">
                          <th colSpan={2} rowSpan={2} className="px-4 py-3 text-center font-semibold align-middle">Nominal Size</th>
                          <th colSpan={2} rowSpan={2} className="px-4 py-3 text-center font-semibold align-middle">Mean Outside Diameter (mm)</th>
                          <th colSpan={4} className="px-4 py-3 text-center font-semibold">SDR 11</th>
                          <th colSpan={4} className="px-4 py-3 text-center font-semibold">SDR 13.5</th>
                        </tr>
                        <tr className="bg-slate-100 text-[#0a2a5e]">
                          <th colSpan={2} className="px-4 py-2 text-center font-semibold">Wall Thickness (mm)</th>
                          <th colSpan={2} className="px-4 py-2 text-center font-semibold">Working Pressure (kgf/cm²)</th>
                          <th colSpan={2} className="px-4 py-2 text-center font-semibold">Wall Thickness (mm)</th>
                          <th colSpan={2} className="px-4 py-2 text-center font-semibold">Working Pressure (kgf/cm²)</th>
                        </tr>
                        <tr className="bg-slate-50 text-slate-500 text-xs">
                          <th className="px-4 py-2 text-center font-medium">mm</th>
                          <th className="px-4 py-2 text-center font-medium">inch</th>
                          <th className="px-4 py-2 text-center font-medium">Min</th>
                          <th className="px-4 py-2 text-center font-medium">Max</th>
                          <th className="px-4 py-2 text-center font-medium">Min</th>
                          <th className="px-4 py-2 text-center font-medium">Max</th>
                          <th className="px-4 py-2 text-center font-medium">27°C</th>
                          <th className="px-4 py-2 text-center font-medium">82°C</th>
                          <th className="px-4 py-2 text-center font-medium">Min</th>
                          <th className="px-4 py-2 text-center font-medium">Max</th>
                          <th className="px-4 py-2 text-center font-medium">27°C</th>
                          <th className="px-4 py-2 text-center font-medium">82°C</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.specTable.rows.map((r, i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <td className="px-4 py-2.5 text-slate-800 font-semibold text-center">{r.mm}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.inch}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.odMin}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.odMax}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s11wMin}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s11wMax}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center font-medium">{r.s11p27}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s11p82}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s13wMin}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s13wMax}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center font-medium">{r.s13p27}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s13p82}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {product.specTable && product.specTable.variant === "table" && (
                <div>
                  <h3 className="text-lg md:text-xl font-black text-[#0a2a5e] mb-1">{product.specTable.title}</h3>
                  {product.specTable.subtitle && (
                    <p className="text-sm text-slate-500 mb-4">{product.specTable.subtitle}</p>
                  )}
                  <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto bg-white">
                    <table className="w-full text-sm min-w-[720px] border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200" data-testid="generic-spec-table">
                      <thead>
                        <tr className="bg-slate-100 text-[#0a2a5e]">
                          {product.specTable.columns.map((c, i) => (
                            <th key={i} className="px-4 py-3 text-center font-semibold">{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {product.specTable.rows.map((r, i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            {r.map((v, j) => (
                              <td key={j} className={`px-4 py-2.5 text-center ${j === 0 ? "text-slate-800 font-semibold" : "text-slate-700"}`}>{v}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {product.specTable && product.specTable.variant === "tables" && (
                <div>
                  <h3 className="text-lg md:text-xl font-black text-[#0a2a5e] mb-1">{product.specTable.title}</h3>
                  {product.specTable.subtitle && (
                    <p className="text-sm text-slate-500 mb-4">{product.specTable.subtitle}</p>
                  )}
                  <div className="space-y-8">
                    {product.specTable.tables.map((t, ti) => (
                      <div key={ti}>
                        <h4 className="text-sm md:text-base font-bold text-[#0a2a5e]" data-testid={`spec-tables-section-${ti}`}>{t.heading}</h4>
                        {t.note && <p className="text-xs text-slate-500 mb-2">{t.note}</p>}
                        <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto bg-white mt-2">
                          <table className="w-full text-sm min-w-[860px] border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200">
                            <thead>
                              <tr className="bg-slate-100 text-[#0a2a5e]">
                                {t.columns.map((c, i) => (
                                  <th key={i} className="px-3 py-3 text-center font-semibold text-xs">{c}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {t.rows.map((r, i) => (
                                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                                  {r.map((v, j) => (
                                    <td key={j} className={`px-3 py-2.5 text-center ${j === 0 ? "text-slate-800 font-semibold" : "text-slate-700"}`}>{v}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.specTable && product.specTable.variant === "uds" && (
                <div>
                  <h3 className="text-lg md:text-xl font-black text-[#0a2a5e] mb-1">{product.specTable.title}</h3>
                  {product.specTable.subtitle && (
                    <p className="text-sm text-slate-500 mb-4">{product.specTable.subtitle}</p>
                  )}
                  <div className="space-y-8">
                    {product.specTable.sections.map((section, si) => (
                      <div key={si}>
                        <h4 className="text-sm md:text-base font-bold text-[#0a2a5e] mb-2" data-testid={`uds-spec-section-${si}`}>{section.heading}</h4>
                        <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto bg-white">
                          <table className="w-full text-sm min-w-[760px] border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200">
                            <thead>
                              <tr className="bg-slate-100 text-[#0a2a5e]">
                                <th colSpan={2} className="px-4 py-3 text-center font-semibold">Nominal Outside Diameter &quot;D&quot;</th>
                                <th colSpan={2} className="px-4 py-3 text-center font-semibold">Mean Outside Diameter (mm)</th>
                                <th colSpan={2} className="px-4 py-3 text-center font-semibold">Outside Diameter at any point (mm)</th>
                                <th colSpan={2} className="px-4 py-3 text-center font-semibold">Wall Thickness &quot;T&quot; (mm)</th>
                              </tr>
                              <tr className="bg-slate-50 text-slate-500 text-xs">
                                <th className="px-4 py-2 text-center font-medium">MM</th>
                                <th className="px-4 py-2 text-center font-medium">Inch</th>
                                <th className="px-4 py-2 text-center font-medium">Min</th>
                                <th className="px-4 py-2 text-center font-medium">Max</th>
                                <th className="px-4 py-2 text-center font-medium">Min</th>
                                <th className="px-4 py-2 text-center font-medium">Max</th>
                                <th className="px-4 py-2 text-center font-medium">Min</th>
                                <th className="px-4 py-2 text-center font-medium">Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.rows.map((r, i) => (
                                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                                  <td className="px-4 py-2.5 text-slate-800 font-semibold text-center">{r.mm}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center">{r.inch}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center">{r.meanMin}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center">{r.meanMax}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center">{r.anyMin}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center">{r.anyMax}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center font-medium">{r.wMin}</td>
                                  <td className="px-4 py-2.5 text-slate-700 text-center font-medium">{r.wMax}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.specTable && !product.specTable.variant && (
                <div>
                  <h3 className="text-lg md:text-xl font-black text-[#0a2a5e] mb-1">{product.specTable.title}</h3>
                  {product.specTable.subtitle && (
                    <p className="text-sm text-slate-500 mb-4">{product.specTable.subtitle}</p>
                  )}
                  <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto bg-white">
                    <table className="w-full text-sm min-w-[900px] border-collapse [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200">
                      <thead>
                        <tr className="bg-slate-100 text-[#0a2a5e]">
                          <th colSpan={2} rowSpan={2} className="px-4 py-3 text-center font-semibold align-middle">Nominal Size</th>
                          <th rowSpan={2} className="px-4 py-3 text-center font-semibold align-middle">Nominal outside diameter &quot;D&quot;</th>
                          <th colSpan={3} className="px-4 py-3 text-center font-semibold">Schedule 40</th>
                          <th colSpan={3} className="px-4 py-3 text-center font-semibold">Schedule 80</th>
                        </tr>
                        <tr className="bg-slate-100 text-[#0a2a5e]">
                          <th className="px-4 py-2 text-center font-semibold align-middle">Wall thickness &quot;T&quot;</th>
                          <th colSpan={2} className="px-4 py-2 text-center font-semibold">Working Pressure</th>
                          <th className="px-4 py-2 text-center font-semibold align-middle">Wall thickness &quot;T&quot;</th>
                          <th colSpan={2} className="px-4 py-2 text-center font-semibold">Working Pressure</th>
                        </tr>
                        <tr className="bg-slate-50 text-slate-500 text-xs">
                          <th className="px-4 py-2 text-center font-medium">mm</th>
                          <th className="px-4 py-2 text-center font-medium">inch</th>
                          <th className="px-4 py-2 text-center font-medium">mm</th>
                          <th className="px-4 py-2 text-center font-medium">mm</th>
                          <th className="px-4 py-2 text-center font-medium">kgf/cm²</th>
                          <th className="px-4 py-2 text-center font-medium">PSI</th>
                          <th className="px-4 py-2 text-center font-medium">mm</th>
                          <th className="px-4 py-2 text-center font-medium">kgf/cm²</th>
                          <th className="px-4 py-2 text-center font-medium">PSI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.specTable.rows.map((r, i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <td className="px-4 py-2.5 text-slate-800 font-semibold text-center">{r.mm}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.inch}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.od}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s40w}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center font-medium">{r.s40k}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s40p}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s80w}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center font-medium">{r.s80k}</td>
                            <td className="px-4 py-2.5 text-slate-700 text-center">{r.s80p}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "installation" && (
            <div>
              <div className="max-w-2xl mb-10">
                <h3 className="text-2xl md:text-3xl font-black text-[#0a2a5e] mb-2">Install with Confidence. Maintain with Ease.</h3>
                <p className="text-slate-600">Follow these recommended steps for a leak-free, long-lasting BAISHALII installation.</p>
              </div>
              <div className="relative">
                <div className="hidden md:block absolute top-7 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 via-sky-400 to-[#0a2a5e]" />
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 relative">
                  {getInstallationSteps(product).map((s, i) => (
                    <div key={i} className="relative bg-white rounded-2xl p-6 border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all duration-300">
                      <div className="relative mb-4 inline-flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-sky-500 to-[#0a2a5e] text-white font-black text-xl shadow-md ring-4 ring-white">
                        {i + 1}
                      </div>
                      <div className="text-xs font-semibold text-sky-600 tracking-wider mb-1">STEP {i + 1}</div>
                      <h4 className="font-black text-[#0a2a5e] text-lg mb-2 leading-tight">{s.title}</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-10 p-6 rounded-2xl bg-gradient-to-r from-sky-50 via-white to-sky-50 border border-sky-100 flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                  <Wrench className="h-6 w-6 text-sky-600" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0a2a5e] mb-1">Need on-site help?</h4>
                  <p className="text-sm text-slate-600 mb-3">Our certified BAISHALII technicians can guide your plumber/contractor on first installation.</p>
                  <Button asChild size="sm" className="bg-[#0a2a5e] hover:bg-[#061a3d] text-white rounded-full">
                    <Link to="/contact">Request Site Support <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
              <div>
                <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-2">RELATED PRODUCTS</div>
                <h2 className="text-3xl md:text-4xl font-black text-[#0a2a5e]">More in {product.categoryName}</h2>
              </div>
              <Button asChild variant="outline" className="rounded-full border-[#0a2a5e] text-[#0a2a5e] hover:bg-[#0a2a5e] hover:text-white">
                <Link to={`/products#${product.categoryId}`}>View All <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p, i) => (
                <Link
                  key={i}
                  to={`/products/${p.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:border-sky-300 transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 flex items-center justify-center p-3">
                    <LazyImage src={p.image} alt={p.name} className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-black text-[#0a2a5e] group-hover:text-sky-600 transition-colors mb-1">{p.name}</h3>
                    <p className="text-xs text-slate-500 leading-snug line-clamp-2">{p.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e] to-sky-700 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">Need help choosing the right pipe?</h2>
          <p className="text-slate-200 mb-8 max-w-2xl mx-auto">Our piping experts are ready to assist you with sizing, specifications and on-site recommendations.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-white text-[#0a2a5e] hover:bg-sky-50 rounded-full px-7 font-bold">
              <Link to="/contact">Talk to an Expert</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7 bg-white/5 backdrop-blur border-white/30 text-white hover:bg-white hover:text-[#0a2a5e]">
              <Link to="/products">Browse all products</Link>
            </Button>
          </div>
        </div>
      </section>
      {/* SKU DETAIL DIALOG */}
      <Dialog open={!!selectedSku} onOpenChange={(o) => !o && setSelectedSku(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
          <DialogTitle className="sr-only">{selectedSku?.name || "SKU Details"}</DialogTitle>
          {selectedSku && (
            <div>
              <div className="grid md:grid-cols-[280px_1fr] gap-0">
                <div className="aspect-square md:aspect-auto md:h-full min-h-[280px] bg-gradient-to-br from-sky-50 to-slate-100 overflow-hidden flex items-center justify-center p-6">
                  <img src={selectedSku.image} alt={selectedSku.name} loading="eager" decoding="async" className="max-w-full max-h-full w-auto h-auto object-contain" />
                </div>
                <div className="p-6 md:p-7">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-2.5 py-1 text-[11px] font-bold rounded-full bg-sky-100 text-sky-700">{selectedSku.type}</span>
                    <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-700">{selectedSku.grade}</span>
                  </div>
                  <h3 className="text-2xl font-black text-[#0a2a5e] leading-tight mb-2">{selectedSku.name}</h3>
                  <p className="text-sm text-sky-600 font-semibold mb-3">{selectedSku.desc}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {selectedSku.longDesc || getSkuLongDesc(selectedSku.type, selectedSku._material) || selectedSku.desc}
                  </p>
                  {selectedSku.hsn && (
                    <div className="mt-3 text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">HSN Code:</span> {selectedSku.hsn}
                    </div>
                  )}
                </div>
              </div>

              {/* Size & Code Table */}
              {getSkuSizes(selectedSku).length > 0 && (
              <div className="px-6 md:px-7">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-[#0a2a5e] tracking-wider">SIZES &amp; PRODUCT CODES</h4>
                  <span className="text-xs text-slate-500">{getSkuSizes(selectedSku).length} variants</span>
                </div>
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  {(() => {
                    const sizes = getSkuSizes(selectedSku);
                    const hasLength = sizes.some((s) => s.length);
                    return (
                      <table className="w-full text-sm">
                        <thead className="bg-gradient-to-r from-[#0a2a5e] to-sky-700 text-white">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold">Size (inch)</th>
                            <th className="px-4 py-3 text-left font-semibold">Size (mm)</th>
                            {hasLength && <th className="px-4 py-3 text-left font-semibold">Length</th>}
                            <th className="px-4 py-3 text-left font-semibold">Product Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sizes.map((s, i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                              <td className="px-4 py-2.5 text-slate-800 font-semibold">{s.inch}</td>
                              <td className="px-4 py-2.5 text-slate-700">{s.mm}</td>
                              {hasLength && <td className="px-4 py-2.5 text-slate-700">{s.length || "—"}</td>}
                              <td className="px-4 py-2.5 font-mono text-xs text-sky-700">{s.code}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
              )}

              <div className="px-6 md:px-7 pb-6 md:pb-7">
                <div className="mt-5 flex flex-wrap gap-3 justify-end">
                  <Button variant="outline" onClick={() => setSelectedSku(null)} className="rounded-full border-slate-300">
                    Close
                  </Button>
                  <Button asChild className="bg-gradient-to-r from-sky-500 to-[#0a2a5e] text-white rounded-full">
                    <Link to="/contact" onClick={() => setSelectedSku(null)}>
                      Enquire about {selectedSku.name.split(" ")[0]} <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
